import { CountryCodeSchema } from '../countries';
import { z } from 'zod/v3';
import { RiskMatrixCountDTOSchema } from './risk-register.dto';
import { VendorAuthenticationMethodSchema, VendorEnvironmentAccessSchema, VendorAssessmentStatusSchema } from '../generated/enums';

// ========== Vendor Management DTOs (RFC-141) ==========

export const VendorCategoryEnum = z.enum([
  'CLOUD_INFRASTRUCTURE', 'IDENTITY', 'DATABASE', 'MONITORING',
  'CI_CD', 'COMMUNICATION', 'AI', 'HR', 'SECURITY', 'OTHER',
]);
export const VendorRiskTierEnum = z.enum(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']);
/** RFC-232 — one axis of the vendor Impact × Likelihood model. */
export const VendorRiskLevelEnum = z.enum(['LOW', 'MEDIUM', 'HIGH']);
export type VendorRiskLevel = z.infer<typeof VendorRiskLevelEnum>;
export const VendorTierSourceEnum = z.enum(['CATEGORY_DEFAULT', 'AI_SUGGESTED', 'MANUAL']);
export const VendorBusinessCriticalityEnum = z.enum(['LOW', 'MEDIUM', 'HIGH']);
export const VendorStatusEnum = z.enum([
  'ACTIVE', 'UNDER_REVIEW', 'OFFBOARDED', 'PENDING_ONBOARDING',
]);
/** HIPAA Business Associate Agreement lifecycle state. */
export const BAAStatusEnum = z.enum([
  'NOT_REQUIRED', 'REQUIRED', 'SIGNED', 'EXPIRED',
]);
export type BAAStatus = z.infer<typeof BAAStatusEnum>;

export const VendorDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  programId: z.string(),
  sequenceNumber: z.number(),
  displayId: z.string(), // "VND-001"
  name: z.string(),
  service: z.string(),
  logoUrl: z.string().nullable().default(null),
  logoEditable: z.boolean().default(false),
  contactName: z.string().trim().max(200).nullable().default(null),
  contactInfo: z.string().trim().max(1000).nullable().default(null),
  headquarters: z.string().trim().max(200).nullable().default(null),
  headquartersCountryCode: CountryCodeSchema.nullable().default(null),
  vendorToCompanyAuthMethod: VendorAuthenticationMethodSchema.nullable().default(null),
  companyToVendorAuthMethod: VendorAuthenticationMethodSchema.nullable().default(null),
  environmentAccess: VendorEnvironmentAccessSchema.nullable().default(null),
  category: VendorCategoryEnum,
  riskTier: VendorRiskTierEnum, // Inherent risk — derived from the axes (RFC-232)
  tierSource: VendorTierSourceEnum,
  tierRationale: z.string().nullable(),
  // RFC-232 Impact × Likelihood axes. Null likelihood = pending assessment;
  // null axes on older vendors = pre-backfill (tier stands alone).
  inherentLikelihood: VendorRiskLevelEnum.nullable(),
  inherentImpact: VendorRiskLevelEnum.nullable(),
  inherentScore: z.number().nullable(),
  residualRiskTier: VendorRiskTierEnum.nullable(), // Residual risk (null = unassessed)
  residualTierSource: VendorTierSourceEnum.nullable(),
  residualTierRationale: z.string().nullable(),
  residualLikelihood: VendorRiskLevelEnum.nullable(),
  residualImpact: VendorRiskLevelEnum.nullable(),
  residualScore: z.number().nullable(),
  businessCriticality: VendorBusinessCriticalityEnum.nullable(),
  suggestedRiskTier: VendorRiskTierEnum.nullable(),
  suggestedTierRationale: z.string().nullable(),
  suggestedTierSignals: z.unknown().nullable(),
  suggestedTierAt: z.coerce.date().nullable(),
  customerDataInvolved: z.boolean(),
  dataProcessingRole: z.string().nullable(),
  ownerId: z.string().nullable(),
  ownerLabel: z.string(),
  ownerRoleName: z.string().nullable(),
  status: VendorStatusEnum,
  onboardedAt: z.coerce.date().nullable(),
  lastReviewDate: z.coerce.date().nullable(),
  nextReviewDate: z.coerce.date().nullable(),
  contractRenewalDate: z.coerce.date().nullable(),
  offboardedAt: z.coerce.date().nullable(),
  securityReview: z.string().nullable(),
  contractualTerms: z.string().nullable(),
  // HIPAA Business Associate tracking — meaningful only when the
  // workspace has HIPAA in enabledFrameworks. SOC 2-only workspaces
  // leave containsEphi=false and baaStatus=NOT_REQUIRED.
  containsEphi: z.boolean(),
  baaStatus: BAAStatusEnum,
  baaSignedAt: z.coerce.date().nullable(),
  baaExpiresAt: z.coerce.date().nullable(),
  baaDocumentUrl: z.string().nullable(),
  // Cross-framework subcontractor / subprocessor chain.
  parentVendorId: z.string().nullable(),
  provider: z.string(),
  confidence: z.string().nullable(),
  evidence: z.string().nullable(),
  notes: z.string().nullable(),
  externalUrl: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type VendorDTO = z.infer<typeof VendorDTOSchema>;

export const CreateVendorDTOSchema = z.object({
  name: z.string().min(1).max(200),
  service: z.string().min(1).max(500),
  contactName: z.string().trim().max(200).nullable().optional(),
  contactInfo: z.string().trim().max(1000).nullable().optional(),
  headquarters: z.string().trim().max(200).nullable().optional(),
  headquartersCountryCode: CountryCodeSchema.nullable().optional(),
  vendorToCompanyAuthMethod: VendorAuthenticationMethodSchema.nullable().optional(),
  companyToVendorAuthMethod: VendorAuthenticationMethodSchema.nullable().optional(),
  environmentAccess: VendorEnvironmentAccessSchema.nullable().optional(),
  category: VendorCategoryEnum,
  // RFC-232 §5.1: optional — when absent the server derives Impact from the
  // form's signals and leaves Likelihood pending assessment.
  riskTier: VendorRiskTierEnum.optional(),
  tierRationale: z.string().nullable().optional(),
  businessCriticality: VendorBusinessCriticalityEnum.nullable().optional(),
  customerDataInvolved: z.boolean().optional(),
  dataProcessingRole: z.string().nullable().optional(),
  ownerId: z.string().nullable().optional(),
  status: VendorStatusEnum.optional(),
  contractRenewalDate: z.coerce.date().nullable().optional(),
  notes: z.string().nullable().optional(),
  externalUrl: z.string().nullable().optional(),
  // HIPAA BAA fields (optional at create time)
  containsEphi: z.boolean().optional(),
  baaStatus: BAAStatusEnum.optional(),
  baaSignedAt: z.coerce.date().nullable().optional(),
  baaExpiresAt: z.coerce.date().nullable().optional(),
  baaDocumentUrl: z.string().nullable().optional(),
  parentVendorId: z.string().nullable().optional(),
});
export type CreateVendorDTO = z.infer<typeof CreateVendorDTOSchema>;

export const UpdateVendorDTOSchema = z.object({
  logoUrl: z.string().url().regex(/^https?:\/\//i, 'Must be an http(s) URL').nullable().optional(),
  contactName: z.string().trim().max(200).nullable().optional(),
  contactInfo: z.string().trim().max(1000).nullable().optional(),
  headquarters: z.string().trim().max(200).nullable().optional(),
  headquartersCountryCode: CountryCodeSchema.nullable().optional(),
  vendorToCompanyAuthMethod: VendorAuthenticationMethodSchema.nullable().optional(),
  companyToVendorAuthMethod: VendorAuthenticationMethodSchema.nullable().optional(),
  environmentAccess: VendorEnvironmentAccessSchema.nullable().optional(),
  name: z.string().min(1).max(200).optional(),
  service: z.string().min(1).max(500).optional(),
  category: VendorCategoryEnum.optional(),
  businessCriticality: VendorBusinessCriticalityEnum.nullable().optional(),
  customerDataInvolved: z.boolean().optional(),
  dataProcessingRole: z.string().nullable().optional(),
  ownerId: z.string().nullable().optional(),
  ownerLabel: z.string().optional(),
  status: VendorStatusEnum.optional(),
  onboardedAt: z.coerce.date().nullable().optional(),
  lastReviewDate: z.coerce.date().nullable().optional(),
  nextReviewDate: z.coerce.date().nullable().optional(),
  contractRenewalDate: z.coerce.date().nullable().optional(),
  offboardedAt: z.coerce.date().nullable().optional(),
  securityReview: z.string().nullable().optional(),
  contractualTerms: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  externalUrl: z.string().nullable().optional(),
  /** Rendered as a link on the public Trust Center — http(s) only, never `javascript:`. */
  trustCenterUrl: z.string().url().regex(/^https?:\/\//i, 'Must be an http(s) URL').nullable().optional(),
  // HIPAA BAA fields
  containsEphi: z.boolean().optional(),
  baaStatus: BAAStatusEnum.optional(),
  baaSignedAt: z.coerce.date().nullable().optional(),
  baaExpiresAt: z.coerce.date().nullable().optional(),
  baaDocumentUrl: z.string().nullable().optional(),
  parentVendorId: z.string().nullable().optional(),
});
export type UpdateVendorDTO = z.infer<typeof UpdateVendorDTOSchema>;

export const VendorListFilterSchema = z.object({
  search: z.string().optional(),
  riskTier: VendorRiskTierEnum.optional(),
  status: VendorStatusEnum.optional(),
  category: VendorCategoryEnum.optional(),
  customerDataInvolved: z.boolean().optional(),
  includeOffboarded: z.boolean().optional(),
  // HIPAA-specific filters
  containsEphi: z.boolean().optional(),
  baaStatus: BAAStatusEnum.optional(),
  // Pagination
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(100).optional(),
});

export const VendorStatsDTOSchema = z.object({
  total: z.number(),
  highRisk: z.number(),
  active: z.number(),
  needsReview: z.number(),
});
export type VendorStatsDTO = z.infer<typeof VendorStatsDTOSchema>;

/** Workspace-wide inventory overview. Progress, categories and matrices exclude offboarded vendors. */
export const VendorDistributionDTOSchema = z.object({
  byStatus: z.array(z.object({ key: z.string(), count: z.number() })),
  byRiskTier: z.array(z.object({ key: z.string(), count: z.number() })),
  byResidualTier: z.array(z.object({ key: z.string(), count: z.number() })),
  byCategory: z.array(z.object({ key: z.string(), count: z.number() })),
  assessmentProgress: z.object({ notStarted: z.number(), inProgress: z.number(), awaitingApproval: z.number(), approved: z.number(), rejected: z.number() }),
  inherentMatrix: z.array(RiskMatrixCountDTOSchema),
  assessedMatrix: z.array(RiskMatrixCountDTOSchema),
});
export type VendorDistributionDTO = z.infer<typeof VendorDistributionDTOSchema>;

// ========== 供应商 CSV / Excel 导入（#1430）==========

/** 下载模板和后端解析共用同一组固定表头。 */
export const VENDOR_IMPORT_REQUIRED_COLUMN_HEADERS = {
  name: 'Vendor Name',
  service: 'Service',
  category: 'Category',
  customerDataInvolved: 'Customer Data Involved',
  owner: 'Owner',
} as const;

export const VENDOR_IMPORT_COLUMN_HEADERS = {
  ...VENDOR_IMPORT_REQUIRED_COLUMN_HEADERS,
  contactName: 'Contact Name',
  contactInfo: 'Contact Info',
  headquarters: 'Headquarters',
  vendorToCompanyAuthMethod: 'Vendor to Company Authentication',
  companyToVendorAuthMethod: 'Company to Vendor Authentication',
  environmentAccess: 'Environment Access',
  headquartersCountryCode: 'Headquarters Country Code',
} as const;

export const VendorImportPreviewDTOSchema = z.object({
  recordCount: z.number().int().nonnegative(),
});
export type VendorImportPreviewDTO = z.infer<typeof VendorImportPreviewDTOSchema>;

export const VendorImportErrorDTOSchema = z.object({
  row: z.number().int().positive(),
  name: z.string().optional(),
  message: z.string(),
});
export type VendorImportErrorDTO = z.infer<typeof VendorImportErrorDTOSchema>;

export const VendorImportResultDTOSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  errors: z.array(VendorImportErrorDTOSchema),
});
export type VendorImportResultDTO = z.infer<typeof VendorImportResultDTOSchema>;

// ========== Vendor discovery (issue #971 M1) ==========

/** Where a discovered-vendor candidate's signal came from. */
export const DiscoveredVendorSourceDTOSchema = z.object({
  type: z.enum(['integration_resource', 'integration_connection', 'asset']),
  detail: z.string(),
});
export type DiscoveredVendorSourceDTO = z.infer<
  typeof DiscoveredVendorSourceDTOSchema
>;

/**
 * A vendor candidate derived from already-connected integration data, not yet
 * created as a Vendor. Surfaced for human review (accept → import / dismiss).
 */
export const DiscoveredVendorCandidateDTOSchema = z.object({
  /** Stable key for this candidate within the workspace (normalized name). */
  key: z.string(),
  name: z.string(),
  service: z.string(),
  category: VendorCategoryEnum,
  website: z.string().nullable(),
  suggestedTier: VendorRiskTierEnum,
  customerDataInvolved: z.boolean(),
  sources: z.array(DiscoveredVendorSourceDTOSchema).min(1),
  /** Fingerprint of the current signals; echoed back when dismissing. */
  signalFingerprint: z.string(),
});
export type DiscoveredVendorCandidateDTO = z.infer<
  typeof DiscoveredVendorCandidateDTOSchema
>;

/** Input for dismissing a discovered candidate from the review panel. */
export const DismissDiscoveredVendorDTOSchema = z.object({
  candidateKey: z.string().min(1),
  signalFingerprint: z.string(),
});
export type DismissDiscoveredVendorDTO = z.infer<
  typeof DismissDiscoveredVendorDTOSchema
>;

/** Input for accepting (importing) a discovered candidate as a real vendor. */
export const AcceptDiscoveredVendorDTOSchema = z.object({
  name: z.string().min(1).max(200),
  service: z.string().min(1).max(200),
  category: VendorCategoryEnum,
  suggestedTier: VendorRiskTierEnum,
  website: z.string().nullable().optional(),
  customerDataInvolved: z.boolean().optional(),
});
export type AcceptDiscoveredVendorDTO = z.infer<
  typeof AcceptDiscoveredVendorDTOSchema
>;

// ========== Vendor document stash (RFC-198 Phase 1) ==========

/** The kinds of document a vendor can have on file. Mirrors the server-side
 * `VendorDocumentType` used by the extraction pipeline. */
export const VendorDocumentTypeEnum = z.enum([
  'trust_center',
  'soc2_report',
  'iso27001_cert',
  'security_questionnaire',
  'dpa',
  'msa',
  'sla',
  'tos',
  'nda',
  'pentest_report',
  'privacy_policy',
  'ai_data_policy',
  'vuln_mgmt_policy',
  'subprocessor_list',
  'other',
]);
export type VendorDocumentType = z.infer<typeof VendorDocumentTypeEnum>;

/** A reference to an assessment a stash document is associated with. */
export const VendorDocumentAssessmentRefSchema = z.object({
  id: z.string(),
  version: z.number(),
  status: VendorAssessmentStatusSchema,
});
export type VendorDocumentAssessmentRef = z.infer<
  typeof VendorDocumentAssessmentRefSchema
>;

/** A document in the vendor's persistent document stash. */
export const VendorDocumentDTOSchema = z.object({
  id: z.string(),
  title: z.string(),
  fileName: z.string().nullable(),
  fileMimeType: z.string().nullable(),
  fileSize: z.number().nullable(),
  documentType: VendorDocumentTypeEnum,
  uploadedAt: z.coerce.date(),
  uploadedByName: z.string().nullable(),
  status: z.string(),
  effectiveFrom: z.coerce.date().nullable(),
  effectiveTo: z.coerce.date().nullable(),
  /** Assessments this document feeds. Empty array = unassigned stash document. */
  assessments: z.array(VendorDocumentAssessmentRefSchema),
  /** How the document entered the stash (RFC-228 §7.4 provenance chip). */
  source: z.enum(['web', 'cli', 'trust-api', 'snapshot', 'unknown']),
  /** Present when fetched from a vendor's Trust API. */
  trustApi: z
    .object({
      publisherName: z.string().nullable(),
      apiBase: z.string(),
      fetchedAt: z.string(),
      attestedMatch: z.boolean().nullable(),
    })
    .nullable(),
  /** RFC-231 §6.3 — present when the document is a user-initiated snapshot of a public page. */
  snapshot: z
    .object({
      sourceUrl: z.string(),
      fetchedAt: z.string(),
      sha256: z.string(),
      renderedAs: z.enum(['pdf', 'markdown']),
    })
    .nullable(),
});
export type VendorDocumentDTO = z.infer<typeof VendorDocumentDTOSchema>;

/** Input for uploading a document to the vendor stash (flag-free). */
export const UploadVendorDocumentDTOSchema = z.object({
  vendorId: z.string(),
  filename: z.string().min(1).max(500),
  contentType: z.string().min(1).max(200),
  base64Data: z.string().min(1),
  size: z.number().int().positive(),
  documentType: VendorDocumentTypeEnum.default('other'),
  /**
   * Satisfy this assessment's matching evidence request. Omit to fall back to
   * the vendor's open (editable) assessment, so a Documents-tab upload still
   * clears the intake checklist.
   */
  assessmentId: z.string().optional(),
});
export type UploadVendorDocumentDTO = z.infer<
  typeof UploadVendorDocumentDTOSchema
>;

// ========== Vendor assessment inputs (RFC-198 Phase 2) ==========

/** A document selected into an assessment's input set. */
export const VendorAssessmentInputDTOSchema = z.object({
  id: z.string(),
  vaultDocumentId: z.string().nullable(),
  documentType: z.string().nullable(),
  includedAt: z.coerce.date(),
  document: z
    .object({
      id: z.string(),
      title: z.string(),
      fileName: z.string().nullable(),
      fileSize: z.number().nullable(),
      status: z.string(),
    })
    .nullable(),
});
export type VendorAssessmentInputDTO = z.infer<
  typeof VendorAssessmentInputDTOSchema
>;

/** Input for replacing an assessment's selected document set. */
export const SetAssessmentInputsDTOSchema = z.object({
  assessmentId: z.string(),
  vaultDocumentIds: z.array(z.string()),
});
export type SetAssessmentInputsDTO = z.infer<
  typeof SetAssessmentInputsDTOSchema
>;

// ── RFC-231 §6: catalog trust links ("where to get the documents") ──
export const VendorTrustDocAccessEnum = z.enum(['public', 'request', 'portal']);
export type VendorTrustDocAccess = z.infer<typeof VendorTrustDocAccessEnum>;
export const VendorTrustDocLinkSchema = z.object({
  url: z.string().url(),
  access: VendorTrustDocAccessEnum,
  /** One-line how-to, required for gated reports (e.g. "AWS Console → Artifact → Reports"). */
  how: z.string().optional(),
  /** Curation note (e.g. "bot-walls plain fetch; opens in a browser"). */
  note: z.string().optional(),
});
export type VendorTrustDocLink = z.infer<typeof VendorTrustDocLinkSchema>;
/** Doc keys are VendorDocumentType values; gated (non-public) links must say how to get the document. */
export const VendorTrustLinksSchema = z.object({
  trustCenterUrl: z.string().url().optional(),
  docs: z.record(VendorDocumentTypeEnum, VendorTrustDocLinkSchema.refine((d) => d.access === 'public' || !!d.how, {
    message: 'gated links need a `how`',
  })).default({}),
});
export type VendorTrustLinks = z.infer<typeof VendorTrustLinksSchema>;

export const SnapshotPublicPageDTOSchema = z.object({
  vendorId: z.string(),
  url: z.string().url(),
  documentType: VendorDocumentTypeEnum.default('other'),
  title: z.string().max(200).optional(),
});
export type SnapshotPublicPageDTO = z.infer<typeof SnapshotPublicPageDTOSchema>;
